import {
  ContentCreator,
  NotificationSystem,
  TemplateLoader,
  getTemplateLoader,
  initializeTemplateSystem,
} from '../utils'

import { ContainerManagerInterface } from '../core/types'
import { ContainerOrchestrator, DemoContainer } from './ContainerOrchestrator'
import { DemoContainerFactory } from './DemoContainerFactory'
import { StatePersistencePlugin } from '../plugins'
import { DemoUIController } from './DemoUIController'
import { StatsController } from './StatsController'

export class ContainerManagerDemo
{
  private readonly notificationSystem: NotificationSystem
  private readonly templateLoader: TemplateLoader
  private readonly contentCreator: ContentCreator
  private readonly demoFactory: DemoContainerFactory
  private uiController: DemoUIController
  private statsController: StatsController
  private containerOrchestrator: ContainerOrchestrator

  private isGlobalPinned: boolean = false

  static async init(): Promise<ContainerManagerDemo>
  {
    await initializeTemplateSystem()
    return new Promise((resolve, reject) => {
      try {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => resolve(new ContainerManagerDemo()))
        } else {
          resolve(new ContainerManagerDemo())
        }
      } catch (e) {
        reject(e)
      }
    })
  }

  constructor()
  {
    this.notificationSystem = new NotificationSystem()
    this.templateLoader = getTemplateLoader()
    this.contentCreator = new ContentCreator(this.templateLoader)
    this.demoFactory = new DemoContainerFactory()

    const app = document.getElementById('app')!
    this.uiController = new DemoUIController(app, {
      onAddStringContainer: () => this.createUserStringContainer(),
      onAddTemplateContainer: () => this.createUserTemplateContainer(),
      onAddElementContainer: () => this.createUserElementContainer(),
      onAddSnappingContainer: () => this.createUserSnappingContainer(),
      onRestoreContainer: () => this.restoreLastClosedContainer(),
      onClearStorage: () => this.clearStorage(),
      onClearAll: () => this.clearAll(),
      onToggleGlobalPin: () => this.toggleGlobalPinnedMode(),
      onSnapStepChange: (step) => this.containerOrchestrator.setAllContainersSnapStep(step),
      onSnapModeChange: (mode) => this.containerOrchestrator.setAllContainersDirectionMode(mode),
    })

    this.statsController = new StatsController(app.querySelector('#statsPanel')!)
    this.containerOrchestrator = new ContainerOrchestrator(
      this.contentCreator,
      this.notificationSystem,
      this.demoFactory,
      () => this.isGlobalPinned,
      (container) => this.onContainerCreated(container),
      () => this.updateUI()
    )

    this.initializeEventBasedSaving()
    this.createDemoContainers().catch(console.error)
    this.updateUI()
  }

  private onContainerCreated(container: DemoContainer): void
  {
    this.statsController.setupContainerHoverEvents(
      container.element,
      container.containerId,
      () => this.getContainerStats(container.manager, container.containerId),
      () => this.statsController.showGlobalStats(() => this.getGlobalStats()),
      () => false // Simplified isAnyContainerDragging
    )

    this.updateUI()
  }

  private initializeEventBasedSaving(): void
  {
    window.addEventListener('beforeunload', () => {
      StatePersistencePlugin.saveAllContainers()
    })

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) StatePersistencePlugin.saveAllContainers()
    })

    window.addEventListener('resize', () => {
      clearTimeout((window as any).resizeSaveTimeout)

      ;(window as any).resizeSaveTimeout = setTimeout(() => {
        StatePersistencePlugin.saveAllContainers()
      }, 500)
    })
  }

  private updateUI(): void {
    this.statsController.updateStats(() => this.getGlobalStats())
    this.uiController.updateRestoreButton(StatePersistencePlugin.getClosedQueue().length)
    this.uiController.updateSnappingStatus()
  }

  private async restoreLastClosedContainer(): Promise<void> {
    if (!StatePersistencePlugin.hasClosedContainers()) {
      this.notificationSystem.show('No closed demo containers to restore', 'warning')
      return
    }

    const containerId = StatePersistencePlugin.popLastClosedContainer()
    if (!containerId) {
      this.notificationSystem.show('Failed to restore container', 'error')
      return
    }

    if (this.containerOrchestrator.containers.some(c => c.containerId === containerId)) {
      this.notificationSystem.show('Container is already open', 'warning')
      StatePersistencePlugin.addToClosedQueue(containerId)
      return
    }

    const savedState = StatePersistencePlugin.getContainerState(containerId)
    if (!savedState) {
      this.notificationSystem.show('Container state not found', 'error')
      return
    }

    try {
      await this.createContainerFromSavedState(containerId, savedState)
      this.notificationSystem.show(`Demo container "${savedState.title || containerId}" restored`, 'success')
      this.updateUI()
    } catch (error) {
      this.notificationSystem.show('Failed to restore container', 'error')
      StatePersistencePlugin.addToClosedQueue(containerId)
    }
  }

  private async createContainerFromSavedState(containerId: string, savedState: any): Promise<void> {
    const content = await this.getContentForSavedState(containerId, savedState)
    const config = this.demoFactory.getDemoConfig(containerId)
    await this.containerOrchestrator.createContainer({
      x: savedState.x,
      y: savedState.y,
      width: savedState.width,
      height: savedState.height,
      content,
      type: savedState.containerType,
      title: savedState.title,
      color: savedState.color,
      draggingDirection: savedState.draggingDirection,
      useSnapping: savedState.useSnapping,
      containerId,
      boundaries: config?.boundaries || savedState.boundaries || {},
      constrainToParent: !!savedState.parentElementId,
      restoreState: true,
      resize: savedState.resize,
      isDemoContainer: this.demoFactory.isDemoContainer(containerId),
      mode: savedState.mode
    })

    StatePersistencePlugin.updateContainerState(containerId, {
      closedTimestamp: 0,
      isClosed: false
    })
  }

  private async getContentForSavedState(
    containerId: string,
    savedState: any
  ): Promise<string | HTMLElement | { template: string }> {
    if (this.demoFactory.isDemoContainer(containerId)) {
      const config = this.demoFactory.getDemoConfig(containerId)
      if (config) return this.demoFactory.createDemoContent(containerId, config)
    }

    if (savedState.containerType === 'template') return { template: 'media' }
    const element = document.createElement('div')
    element.innerHTML = `<h4>${savedState.title || 'Restored'}</h4><p>Restored from localStorage.</p>`;

    return savedState.containerType === 'element' ? element : element.innerHTML
  }

  private async createDemoContainers(): Promise<void>
  {
    const savedStates = StatePersistencePlugin.getAllContainerStates()
    const openContainers = Object.entries(savedStates)
      .filter(([id, state]) => {
        return this.demoFactory.isDemoContainer(id) && !state.isClosed
      })

    if (openContainers.length > 0) {
      for (const [id, state] of openContainers) {
        if (!this.containerOrchestrator.containers.find(c => c.containerId === id)) {
          await this.createContainerFromSavedState(id, state)
        }
      }

      if (this.containerOrchestrator.containers.length > 0) {
        return
      }
    }

    await this.createStringContainer({ containerId: 'demo-string-container', x: 405, y: 475, width: 310, height: 265 })
    await this.createTemplateContainer({ containerId: 'demo-template-container', x: 515, y: 50, width: 305, height: 305 })
    await this.createElementContainer({ containerId: 'demo-element-container', x: 855, y: 385, width: 300, height: 250 })
    await this.createSnappingContainer({ containerId: 'demo-snapping-container', x: 750, y: 555, width: 315, height: 240 })
  }

  private async createStringContainer(params: any): Promise<void> {
    const config = this.demoFactory.getDemoConfig(params.containerId)
    if (!config) return

    const content = await this.demoFactory.createDemoContent(params.containerId, config)
    await this.containerOrchestrator.createContainer({
      ...params,
      content,
      ...config,
      isDemoContainer: true
    })
  }

  private async createTemplateContainer(params: any): Promise<void>
  {
    const config = this.demoFactory.getDemoConfig(params.containerId)
    if (!config) return

    const content = await this.demoFactory.createDemoContent(params.containerId, config)
    const container = await this.containerOrchestrator.createContainer({
      ...params,
      content,
      ...config,
      isDemoContainer: true
    })

    if (config.template) {
      this.demoFactory.addTemplateInfo(container, config.template, config.useSnapping || false)
    }
  }

  private async createElementContainer(params: any): Promise<void>
  {
    const config = this.demoFactory.getDemoConfig(params.containerId)
    if (!config) return

    const content = await this.demoFactory.createDemoContent(params.containerId, config)
    await this.containerOrchestrator.createContainer({
      ...params,
      content,
      ...config,
      isDemoContainer: true
    })
  }

  private async createSnappingContainer(params: any): Promise<void>
  {
    const config = this.demoFactory.getDemoConfig(params.containerId)
    if (!config) return

    const content = await this.demoFactory.createDemoContent(params.containerId, config)
    await this.containerOrchestrator.createContainer({
      ...params,
      content,
      ...config,
      isDemoContainer: true
    })
  }

  private async createUserStringContainer(): Promise<void>
  {
    const id = `user-string-${Date.now()}`
    await this.containerOrchestrator.createContainer({
      x: 50 + Math.random() * 500,
      y: 50 + Math.random() * 300,
      width: 310,
      height: 240,
      title: 'User String',
      color: this.getRandomColor(),
      useSnapping: false,
      containerId: id,
      content: `<h4>User String</h4><p>A user-created container.</p>`,
      type: 'string',
      isDemoContainer: false
    })
  }

  private async createUserTemplateContainer(): Promise<void>
  {
    const id = `user-template-${Date.now()}`
    const templateName = 'media'
    await this.containerOrchestrator.createContainer({
      x: 50 + Math.random() * 500,
      y: 50 + Math.random() * 300,
      width: 320,
      height: 290,
      title: 'User Template',
      color: this.getRandomColor(),
      useSnapping: false,
      containerId: id,
      content: { template: templateName },
      type: 'template',
      isDemoContainer: false
    })
  }

  private async createUserElementContainer(): Promise<void>
  {
    const id = `user-element-${Date.now()}`
    const contentElement = document.createElement('div')
    contentElement.innerHTML = `<h4>User Element</h4><p>A user-created DOM element.</p>`

    await this.containerOrchestrator.createContainer({
      x: 50 + Math.random() * 500,
      y: 50 + Math.random() * 300,
      width: 280,
      height: 260,
      title: 'User DOM Element',
      color: this.getRandomColor(),
      useSnapping: Math.random() > 0.5,
      containerId: id,
      content: contentElement,
      type: 'element',
      isDemoContainer: false
    })
  }

  private async createUserSnappingContainer(): Promise<void>
  {
    const id = `user-snapping-${Date.now()}`
    await this.containerOrchestrator.createContainer({
      x: 50 + Math.random() * 500,
      y: 50 + Math.random() * 300,
      width: 300,
      height: 250,
      title: 'User Snapping',
      color: this.getRandomColor(),
      useSnapping: true,
      containerId: id,
      content: `<h4>User Snapping</h4><p>This container has snapping enabled.</p>`,
      type: 'string',
      isDemoContainer: false
    })
  }

  private clearStorage(): void
  {
    StatePersistencePlugin.clearStorage()
    this.notificationSystem.show('LocalStorage cleared', 'success')
    this.updateUI()

    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }

  private clearAll(): void
  {
    this.containerOrchestrator.destroyAll()
    this.updateUI()

    this.notificationSystem.show('All containers cleared', 'info')
  }

  private toggleGlobalPinnedMode(): void
  {
    this.isGlobalPinned = !this.isGlobalPinned
    this.uiController.pinButton!.classList.toggle('btn-active', this.isGlobalPinned)
    this.containerOrchestrator.toggleGlobalPinnedMode(this.isGlobalPinned)

    this.updateUI()
  }

  private getContainerStats(manager: ContainerManagerInterface, containerId: string): any
  {
    const title = manager.getContainer().dataset.title || containerId

    return {
      activeBlock: title.toLowerCase().replace(/\s+/g, '-'),
      lock: manager.getMode() === 'pinned' ? 'locked' : 'opened',
      direction: manager.getDirection(),
      step: manager.getSnappingConfig?.()?.snapStep,
      hasSnapping: manager.getSnappingConfig?.()?.enabled || false
    }
  }

  private getGlobalStats(): any
  {
    const typeCount = this.containerOrchestrator.containers
      .reduce((acc, { type }) => {
        acc[type] = (acc[type] || 0) + 1
        return acc
      }, {} as Record<string, number>)

    return {
      containerCount: this.containerOrchestrator.containers.length,
      contentTypes: `S:${typeCount.string || 0} T:${typeCount.template || 0} E:${typeCount.element || 0}`,
      pinnedMode: this.isGlobalPinned ? 'Enabled' : 'Disabled',
      snappingCount: this.containerOrchestrator.containers.filter(c => c.hasSnapping).length
    }
  }

  private getRandomColor(): string
  {
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']
    return colors[Math.floor(Math.random() * colors.length)]
  }
}
